import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useNow, useRuntime } from './context';
import { formatMoney, formatStaleness } from '../mirror/time';
import { Analytics } from './Analytics';
import type { RemainderKind, RevenueBySource } from '../wire';
import { useLocale, useT } from '../messages';
import type { T, UiLocale } from '../messages';

/**
 * Reports (module `reporting`, plan MVP 4).
 *
 * **Read-only, and fetched rather than mirrored.** The distinction is argued in
 * `mirror/reports.ts`: a report is a fold over the whole ledger and there is
 * exactly one correct implementation of that fold, in SQL, on the server.
 * Recomputing revenue on the device out of a 60-day mirror would produce a
 * second answer that is wrong in the least visible way possible — simply
 * smaller.
 *
 * So offline this screen shows **the last answer the server gave**, with its age
 * stated in the same words the status strip uses for the book. That is the same
 * honesty §8.1 asks for everywhere else: the device may show a memory, and it
 * must say that is what it is doing.
 *
 * ## Why revenue is split four ways
 *
 * Because §7.4 rule 1 has four dates of money — a deposit on `prepaid_at`, a
 * certificate on `issued_on`, a cash top-up on `occurred_on`, and the counter on
 * `settled_on`. Showing one total would hide the invariant; showing the four
 * means a number that looks wrong names the rule it broke.
 */
export function Reports(): ReactNode {
  const runtime = useRuntime();
  // `useNow` is here for its *tick* — it repaints every twenty seconds so the
  // age below grows without a reload. The number itself comes from the
  // runtime's clock, which is the app's one clock (`runtime.ts`) and the only
  // one a test can pin to a date.
  useNow();
  const now = runtime.now();
  const t = useT();
  const locale = useLocale();
  const [failed, setFailed] = useState(false);

  const session = useLive(() => runtime.mirror.session(), null);
  const currency = session?.tenant.currency ?? 'USD';
  const timezone = session?.tenant.timezone ?? 'UTC';
  const entitled = (session?.modules ?? []).includes('reporting');

  const snapshot = useLive(() => runtime.reports.snapshot(), null);

  // Ask when the screen opens, exactly as the shell asks `sync.start()` when it
  // mounts. The render path above reads IndexedDB and nothing else.
  useEffect(() => {
    if (!entitled) return;
    setFailed(false);
    void runtime.reports.refresh().catch(() => setFailed(true));
  }, [runtime, entitled]);

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('reports.notSwitchedOn')}</p>
      </section>
    );
  }

  if (snapshot === null) {
    return (
      <section className="view">
        <p className="empty">{failed ? t('reports.noneYet') : t('reports.reading')}</p>
      </section>
    );
  }

  const { summary, revenue, mix, weekdays, top, lapsed } = snapshot;

  return (
    <section className="view view--reports">
      <div className="view__header">
        <h1>{t('reports.title')}</h1>
        {/*
          One string, not two nodes: the age and the failure are one sentence
          about the same thing — how much this screen can be trusted — and a
          reader (or a test) should never have to reassemble it.
        */}
        <span className="status-strip__staleness">
          {`${formatStaleness(snapshot.fetchedAt, now, timezone, locale)}${
            failed ? t('reports.couldNotReach') : ''
          }`}
        </span>
      </div>

      <article className="panel">
        <h2>{t('reports.last30', { from: summary.date_from, to: summary.date_to })}</h2>
        <div className="tiles">
          <Tile
            label={t('reports.moneyIn')}
            value={formatMoney(summary.revenue_cents, currency, locale)}
          />
          <Tile
            label={t('reports.givenAway')}
            value={formatMoney(summary.discount_cents, currency, locale)}
          />
          {/*
            **Tips, tax and refunds, as three separate tiles** (0046).

            None of the three is revenue and each is not-revenue for a different
            reason — a tip is money for a person, tax is money for a government,
            a refund is money that went back — so folding them into one "not
            yours" number would be three facts nobody could act on. They are
            drawn only when they are non-zero, which on this island's tenants is
            never for tax: the USVI has no sales tax.
          */}
          {summary.tip_cents > 0 ? (
            <Tile
              label={t('reports.tips')}
              value={formatMoney(summary.tip_cents, currency, locale)}
            />
          ) : null}
          {summary.tax_cents > 0 ? (
            <Tile
              label={t('reports.taxTile')}
              value={formatMoney(summary.tax_cents, currency, locale)}
            />
          ) : null}
          {summary.refund_cents > 0 ? (
            <Tile
              label={t('reports.refundsTile')}
              value={formatMoney(summary.refund_cents, currency, locale)}
            />
          ) : null}
          <Tile label={t('reports.checkouts')} value={String(summary.settlements)} />
          <Tile label={t('reports.visits')} value={String(summary.visits)} />
          <Tile label={t('reports.noShows')} value={String(summary.no_shows)} />
          <Tile label={t('reports.upcoming')} value={String(summary.upcoming)} />
        </div>
        <SourceLine bySource={summary.by_source} currency={currency} t={t} locale={locale} />
        <TenderLine byTender={summary.by_tender} currency={currency} t={t} locale={locale} />
      </article>

      <article className="panel">
        <h2>{t('reports.revenueByMonth')}</h2>
        <div className="table-scroll">
          <table className="report-table">
            <thead>
              <tr>
                <th scope="col">{t('reports.colMonth')}</th>
                <th scope="col">{t('reports.colCounter')}</th>
                <th scope="col">{t('reports.colDeposits')}</th>
                <th scope="col">{t('reports.colCertificates')}</th>
                <th scope="col">{t('reports.colCreditTopups')}</th>
                <th scope="col">{t('reports.colTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {revenue.months.map((month) => (
                <tr key={month.month}>
                  <th scope="row">{month.month}</th>
                  <td>{formatMoney(month.by_source.settlement_cents, currency, locale)}</td>
                  <td>{formatMoney(month.by_source.deposit_cents, currency, locale)}</td>
                  <td>{formatMoney(month.by_source.certificate_cents, currency, locale)}</td>
                  <td>{formatMoney(month.by_source.cash_credit_topup_cents, currency, locale)}</td>
                  <td>
                    <strong>{formatMoney(month.total_cents, currency, locale)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <small className="muted">{t('reports.dateOfMoney')}</small>
      </article>

      <article className="panel">
        <h2>{t('reports.whatSells', { from: mix.date_from, to: mix.date_to })}</h2>
        <div className="table-scroll">
          <table className="report-table">
            <thead>
              <tr>
                <th scope="col">{t('reports.colItem')}</th>
                <th scope="col">{t('reports.colSold')}</th>
                <th scope="col">{t('reports.colAtList')}</th>
                <th scope="col">{t('reports.colGivenAway')}</th>
                <th scope="col">{t('reports.colNet')}</th>
              </tr>
            </thead>
            <tbody>
              {/*
                One heading per treatment, the lengths indented beneath it —
                the owner's ruling 3 of 2026-09-21, and the reason
                `settlement_lines` learned `option_id` at all (migration 0052).
                The heading's numbers stay the **totals**: a sub-row is a part
                of the row above it, never a sibling to be added up, which is
                why the lengths are indented rather than given a column.

                A React fragment rather than a nested table: a sub-row belongs
                to the same grid as its heading, and two tables would let the
                money columns drift out of line the moment one of them held a
                wider number.
              */}
              {mix.items.map((item) => (
                <Fragment key={item.item_id ?? item.name}>
                  <tr>
                    <th scope="row">{item.name}</th>
                    <td>{item.qty}</td>
                    <td>{formatMoney(item.list_cents, currency, locale)}</td>
                    <td>{formatMoney(item.discount_cents, currency, locale)}</td>
                    <td>{formatMoney(item.net_cents, currency, locale)}</td>
                  </tr>
                  {item.options.map((option) => (
                    <tr key={option.option_id} className="muted">
                      {/*
                        `scope="row"` again and the indent in a style rather than
                        a class: the sub-row is still the row's header for a
                        screen reader, and `styles.css` is a file three other
                        surfaces share — one padding value is not worth reaching
                        into it.
                      */}
                      <th scope="row" style={{ paddingLeft: 28 }}>
                        {option.name}
                      </th>
                      <td>{option.qty}</td>
                      <td>{formatMoney(option.list_cents, currency, locale)}</td>
                      <td>{formatMoney(option.discount_cents, currency, locale)}</td>
                      <td>{formatMoney(option.net_cents, currency, locale)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {mix.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    {t('reports.nothingSold')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </article>

      <article className="panel">
        <h2>{t('reports.busiestDays')}</h2>
        <ul className="weekday-list">
          {weekdays.weekdays.map((day) => (
            <li key={day.weekday}>
              {/*
                `name` is the **server's** weekday word, and it arrives in
                English. Printing it verbatim is the honest thing to do with a
                server sentence, but it is also the one residual this wave
                leaves on screen: `core-api`'s `/reports/visits-by-weekday`
                should send only the index and let the client `Intl` it, exactly
                as `Settings` now does for the hours editor. Changing the wire
                is out of scope here, so it is named rather than papered over.
              */}
              <span className="weekday-list__name">{day.name}</span>
              <span
                className="weekday-list__bar"
                style={{ width: `${barWidth(day.visits, weekdays.weekdays)}%` }}
                aria-hidden="true"
              />
              <span>{day.visits}</span>
            </li>
          ))}
        </ul>
      </article>

      <article className="panel">
        <h2>{t('reports.bestGuests')}</h2>
        <ul className="report-list">
          {top.clients.map((client) => (
            <li key={client.client_id}>
              <div>
                <strong>{client.display_name}</strong>
                <small>
                  {t('clients.visits', { count: client.visits })}
                  {client.last_visit_on === null
                    ? ''
                    : t('reports.lastVisit', { date: client.last_visit_on })}
                </small>
              </div>
              <span>{formatMoney(client.revenue_cents, currency, locale)}</span>
            </li>
          ))}
          {top.clients.length === 0 ? <li className="muted">{t('reports.nobodyYet')}</li> : null}
        </ul>
      </article>

      <article className="panel">
        <h2>{t('reports.worthACall')}</h2>
        <ul className="report-list">
          {lapsed.clients.map((client) => (
            <li key={client.client_id}>
              <div>
                <strong>{client.display_name}</strong>
                <small>
                  {/*
                    The threshold is *personal* — 1.5× this guest's own usual gap,
                    LoveSpa's definition, ported unchanged. A shop-wide "90 days"
                    would put the Christmas regular on this list every March and
                    the owner would stop reading it.
                  */}
                  {t('reports.daysSince', {
                    days: client.days_since,
                    usual: client.usual_gap_days,
                  })}
                  {client.phone === null ? '' : t('reports.phoneTail', { phone: client.phone })}
                </small>
              </div>
              <span>{t('clients.visits', { count: client.visits })}</span>
            </li>
          ))}
          {lapsed.clients.length === 0 ? (
            <li className="muted">{t('reports.nobodyOverdue')}</li>
          ) : null}
        </ul>
      </article>

      {/*
        The replica's five, below the ledger's six. Same activity, different
        mechanism — and visibly so: everything above is a snapshot with its age
        stated, everything below is read live from `reporting-api` and says
        plainly when it cannot be. See `Analytics.tsx` for why it is not
        mirrored.
      */}
      <Analytics />
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="tile">
      <span className="tile__value">{value}</span>
      <span className="tile__label">{label}</span>
    </div>
  );
}

function SourceLine({
  bySource,
  currency,
  t,
  locale,
}: {
  bySource: RevenueBySource;
  currency: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  return (
    <p className="muted">
      {t('reports.sourceLine', {
        settlement: formatMoney(bySource.settlement_cents, currency, locale),
        deposit: formatMoney(bySource.deposit_cents, currency, locale),
        certificate: formatMoney(bySource.certificate_cents, currency, locale),
        topup: formatMoney(bySource.cash_credit_topup_cents, currency, locale),
      })}
    </p>
  );
}

/**
 * **How you were paid** (B4) — the counter's money, by tender.
 *
 * One line under the four sources rather than a table, and only the tenders
 * that actually brought money in. A shop that takes cash and Venmo should read
 * a sentence with two numbers in it; printing `$0.00 card` at a business that
 * dropped cards four years ago would be the report arguing with the owner.
 *
 * It says nothing at all when every tender is zero, which is a month with no
 * counter sales — and an empty sentence is more honest there than six zeroes.
 *
 * `Σ` here is `by_source.settlement_cents` above by construction (both fold the
 * same tenders with the same exclusion), so the two lines can be read against
 * each other and a disagreement is a bug rather than a rounding story.
 */
function TenderLine({
  byTender,
  currency,
  t,
  locale,
}: {
  byTender: Partial<Record<RemainderKind, number>>;
  currency: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  const paid = TENDER_ORDER.map((kind) => ({ kind, cents: byTender[kind] ?? 0 })).filter(
    (row) => row.cents !== 0,
  );
  if (paid.length === 0) return null;
  return (
    <p className="muted">
      {t('reports.tenderLine')}
      {paid.map((row, index) => (
        <span key={row.kind}>
          {index === 0 ? ' ' : ' · '}
          {formatMoney(row.cents, currency, locale)} {t(`checkout.tender.${row.kind}`)}
        </span>
      ))}
    </p>
  );
}

/**
 * The order the tenders read in, which is the vocabulary's own rather than the
 * tenant's: this is a report about a month that is over, and a shop that
 * reordered its buttons last week should not have last month's report reorder
 * with them. Exported for Today's takings line, so the counter and the month
 * read their tenders in the same order.
 */
export const TENDER_ORDER: readonly RemainderKind[] = [
  'cash',
  'check',
  'venmo',
  'paypal',
  'card',
  'other',
];

function barWidth(visits: number, all: readonly { visits: number }[]): number {
  const most = Math.max(...all.map((d) => d.visits), 1);
  return Math.round((visits / most) * 100);
}
